import React from 'react';
import PropTypes from 'prop-types';
import { createTextMaskInputElement } from 'text-mask-core';

const MaskedInput = React.forwardRef((props, ref) => {
  const { guide, keepCharPositions, mask, onChange, pipe, placeholderChar, showMask, value, ...other } = props;
  const inputRef = React.useRef(null);
  const textMaskInputRef = React.useRef(null);

  const updateMask = React.useCallback(
    nextValue => {
      if (!inputRef.current || !mask) return;

      const textMaskConfig = {
        guide,
        inputElement: inputRef.current,
        keepCharPositions,
        mask,
        pipe,
        placeholderChar,
        showMask,
      };

      if (!textMaskInputRef.current) {
        textMaskInputRef.current = createTextMaskInputElement(textMaskConfig);
      }

      textMaskInputRef.current.update(nextValue == null ? '' : nextValue, textMaskConfig);
    },
    [guide, keepCharPositions, mask, pipe, placeholderChar, showMask]
  );

  React.useImperativeHandle(ref, () => ({ inputElement: inputRef.current }), []);

  React.useEffect(() => {
    updateMask(value);
  }, [updateMask, value]);

  const handleChange = event => {
    updateMask(event.target.value);

    if (onChange) {
      onChange(event);
    }
  };

  const inputProps = value === undefined ? other : { ...other, value: value == null ? '' : value };

  return <input {...inputProps} ref={inputRef} onChange={handleChange} />;
});

MaskedInput.displayName = 'MaskedInput';

MaskedInput.defaultProps = {
  guide: true,
  keepCharPositions: false,
  placeholderChar: '_',
  showMask: false,
};

MaskedInput.propTypes = {
  guide: PropTypes.bool,
  keepCharPositions: PropTypes.bool,
  mask: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  onChange: PropTypes.func,
  pipe: PropTypes.func,
  placeholderChar: PropTypes.string,
  showMask: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default MaskedInput;